/**
 * The A4 document preview (design InvoicePaper.dc.html, 595 × 842 px at
 * 100%). Used by the editor (live, every keystroke), the detail page and
 * Settings → Branding. It shows whatever figures it is given — the editor
 * passes its invoiceMath preview, the detail page the server's document —
 * and never adds money up itself.
 *
 * The paper is always light, like the printed PDF, whatever the app theme.
 */
import React from 'react';
import QRCode from 'react-qr-code';
import { date, money, num, qty, termLabel } from '../format';
import { divRound, qtyThousandths } from '../math/invoiceMath';

export interface PreviewLine {
  lineId?: string;
  description: string;
  quantity: number;
  unit?: string;
  unitPriceMinor: number;
  discountMinor?: number;
  lineTotalMinor?: number;
  taxRateBp?: number;
}

export interface PreviewDoc {
  docType: 'INVOICE' | 'QUOTATION' | 'CREDIT_NOTE' | 'RECEIPT';
  number?: string | null;
  status?: string;
  isTaxInvoice?: boolean;
  customer?: { name?: string; companyName?: string; address?: string; tin?: string; phoneE164?: string; email?: string } | null;
  seller?: { shopName?: string; address?: string; phone?: string; email?: string; tin?: string; vatNumber?: string } | null;
  issueDate?: string;
  supplyDate?: string;
  dueDate?: string;
  validUntil?: string;
  paymentTerms?: string;
  customTermDays?: number;
  lines: PreviewLine[];
  charges?: { label: string; amountMinor: number }[];
  subtotalMinor: number;
  discountMinor: number;
  chargesMinor: number;
  taxLines: { label?: string; rateBp: number; baseMinor: number; taxMinor: number }[];
  roundingMinor?: number;
  totalMinor: number;
  depositAppliedMinor?: number;
  paidMinor?: number;
  creditedMinor?: number;
  balanceMinor: number;
  amountInWords?: string;
  notes?: string;
  terms?: string;
  shareUrl?: string;
  voidReason?: string;
  currency?: string;
}

export interface PreviewBranding {
  accentColor?: string;
  footerText?: string;
  bankAccounts?: { bankName: string; branch?: string; accountName: string; accountNumber: string }[];
  shopTin?: string;
}

const TITLES: Record<string, string> = { INVOICE: 'INVOICE', QUOTATION: 'QUOTATION', CREDIT_NOTE: 'CREDIT NOTE', RECEIPT: 'RECEIPT' };
const MUTED = '#6A6A76';
const SUB = '#45454F';
const LINE = '#EDEDF1';

/** Accent colours below 4.5:1 on white fall back to the Netlise brand (design L, Branding). */
export function contrastOnWhite(hex: string): number {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return 0;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16) / 255)
    .map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return 1.05 / (lum + 0.05);
}
export const safeAccent = (hex?: string) => (hex && contrastOnWhite(hex) >= 4.5 ? hex : '#4E45E4');

/** A line's amount column: quantity × price less the line's own discount (the invoice discount shows in the totals). */
export const lineAmount = (l: PreviewLine) => divRound(qtyThousandths(l.quantity) * (l.unitPriceMinor || 0), 1000) - (l.discountMinor || 0);

const initials = (name?: string) => (name || 'N').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

function Label({ children }: { children: React.ReactNode }) {
  return <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: '.08em', color: MUTED }}>{children}</span>;
}

export default function InvoicePreview({
  doc, branding = {}, shop, copy, scale = 1,
}: {
  doc: PreviewDoc;
  branding?: PreviewBranding;
  /** Used while the document is a draft and has no seller snapshot yet. */
  shop?: { shopName?: string; address?: string; phoneNumber?: string; email?: string } | null;
  copy?: 'ORIGINAL' | 'DUPLICATE';
  scale?: number;
}) {
  const accent = safeAccent(branding.accentColor);
  const seller = doc.seller ?? { shopName: shop?.shopName, address: shop?.address, phone: shop?.phoneNumber, email: shop?.email };
  const title = doc.isTaxInvoice && doc.docType === 'INVOICE' ? 'TAX INVOICE' : TITLES[doc.docType];
  const isQuote = doc.docType === 'QUOTATION';
  const mark = doc.status === 'VOID' ? 'VOID' : doc.status === 'DRAFT' || !doc.number ? 'DRAFT' : null;
  const cur = doc.currency || 'LKR';
  const bank = branding.bankAccounts?.[0];

  const totals: { l: string; v: string; strong?: boolean; top?: boolean; muted?: boolean }[] = [
    { l: 'Subtotal', v: money(doc.subtotalMinor, cur) },
  ];
  if (doc.discountMinor) totals.push({ l: 'Discount', v: `−${money(doc.discountMinor, cur)}`, muted: true });
  (doc.charges || []).forEach((c) => c.amountMinor && totals.push({ l: c.label, v: money(c.amountMinor, cur) }));
  if (doc.taxLines.length) {
    const before = doc.taxLines.reduce((a, t) => a + t.baseMinor, 0);
    totals.push({ l: doc.isTaxInvoice ? 'Value of supply before VAT' : 'Before tax', v: money(before, cur), strong: doc.isTaxInvoice });
    doc.taxLines.forEach((t) => totals.push({ l: t.label || `VAT ${t.rateBp / 100}%`, v: money(t.taxMinor, cur) }));
  }
  if (doc.roundingMinor) totals.push({ l: 'Rounding', v: money(doc.roundingMinor, cur), muted: true });
  totals.push({ l: doc.isTaxInvoice ? 'Total including VAT' : 'Total', v: money(doc.totalMinor, cur), strong: true, top: true });
  if (doc.depositAppliedMinor) totals.push({ l: 'Deposit received', v: `−${money(doc.depositAppliedMinor, cur)}`, muted: true });
  if (doc.paidMinor) totals.push({ l: 'Paid', v: `−${money(doc.paidMinor, cur)}`, muted: true });
  if (doc.creditedMinor) totals.push({ l: 'Credited', v: `−${money(doc.creditedMinor, cur)}`, muted: true });

  const isVoid = doc.status === 'VOID';
  const balanceLabel = isVoid ? 'Void' : isQuote ? 'Quotation total' : doc.docType === 'INVOICE' ? 'Balance due' : 'Amount';
  const balance = isQuote || doc.docType !== 'INVOICE' ? doc.totalMinor : doc.balanceMinor;

  return (
    <div style={{ width: 595 * scale, height: 842 * scale, flex: 'none' }}>
      <div
        data-testid="invoice-preview"
        style={{
          width: 595, minHeight: 842, transform: `scale(${scale})`, transformOrigin: 'top left',
          boxSizing: 'border-box', background: '#FFFFFF', color: '#131318',
          fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: 9.5, lineHeight: 1.45,
          padding: '38px 40px 30px', display: 'flex', flexDirection: 'column', gap: 18, position: 'relative', overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(19,19,24,.12),0 8px 30px rgba(19,19,24,.08)',
        }}
      >
        {mark && (
          <div style={{ position: 'absolute', top: 300, left: 60, right: 60, textAlign: 'center', fontSize: 84, fontWeight: 700, letterSpacing: '.08em', color: mark === 'VOID' ? '#E0484D' : '#131318', opacity: 0.13, transform: 'rotate(-24deg)', pointerEvents: 'none' }}>
            {mark}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 20 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ width: 38, height: 38, borderRadius: 9, background: accent, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 15 }}>
              {initials(seller?.shopName)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: 12.5, fontWeight: 700 }}>{seller?.shopName || 'Your shop'}</span>
              {seller?.address && <span style={{ color: '#5A5A66' }}>{seller.address}</span>}
              <span style={{ color: '#5A5A66' }}>{[seller?.phone, seller?.email].filter(Boolean).join(' · ')}</span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, textAlign: 'right' }}>
            <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: '.06em', color: accent }}>{title}</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 600 }}>{doc.number || 'Number on issue'}</span>
            {copy && <span style={{ marginTop: 2, padding: '1px 6px', border: '1px solid #131318', borderRadius: 3, fontSize: 8, fontWeight: 700, letterSpacing: '.08em' }}>{copy}</span>}
          </div>
        </div>
        <div style={{ height: 2, background: accent, borderRadius: 2 }} />

        <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Label>BILLED TO</Label>
            <span style={{ fontSize: 11, fontWeight: 600 }}>{doc.customer?.companyName || doc.customer?.name || 'Choose a customer'}</span>
            {doc.customer?.companyName && <span style={{ color: SUB }}>{doc.customer.name}</span>}
            {doc.customer?.address && <span style={{ color: SUB }}>{doc.customer.address}</span>}
            {doc.isTaxInvoice && doc.customer?.tin && (
              <span style={{ color: SUB }}>Buyer TIN <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{doc.customer.tin}</span></span>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 12px', alignContent: 'start' }}>
            <span style={{ color: MUTED }}>Issue date</span><span style={{ textAlign: 'right', fontWeight: 600 }}>{date(doc.issueDate)}</span>
            {doc.isTaxInvoice && (<><span style={{ color: MUTED }}>Date of supply</span><span style={{ textAlign: 'right', fontWeight: 600 }}>{date(doc.supplyDate)}</span></>)}
            {isQuote
              ? (<><span style={{ color: MUTED }}>Valid until</span><span style={{ textAlign: 'right', fontWeight: 600 }}>{date(doc.validUntil)}</span></>)
              : doc.docType === 'INVOICE' && (<><span style={{ color: MUTED }}>Due date</span><span style={{ textAlign: 'right', fontWeight: 600 }}>{date(doc.dueDate)}</span></>)}
            {doc.docType === 'INVOICE' && (<><span style={{ color: MUTED }}>Terms</span><span style={{ textAlign: 'right' }}>{termLabel(doc.paymentTerms, doc.customTermDays)}</span></>)}
            {doc.isTaxInvoice && (<><span style={{ color: MUTED }}>Supplier TIN</span><span style={{ textAlign: 'right', fontFamily: "'JetBrains Mono', monospace" }}>{seller?.tin || branding.shopTin || '—'}</span></>)}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '18px minmax(0,1fr) 44px 64px 52px 72px', gap: 8, padding: '6px 8px', background: '#F3F3F6', borderRadius: 4, fontSize: 8, fontWeight: 700, letterSpacing: '.06em', color: '#5A5A66' }}>
            <span>#</span><span>DESCRIPTION</span><span style={{ textAlign: 'right' }}>QTY</span><span style={{ textAlign: 'right' }}>UNIT PRICE</span><span style={{ textAlign: 'right' }}>DISC.</span><span style={{ textAlign: 'right' }}>AMOUNT</span>
          </div>
          {doc.lines.map((l, i) => (
            <div key={l.lineId || i} style={{ display: 'grid', gridTemplateColumns: '18px minmax(0,1fr) 44px 64px 52px 72px', gap: 8, padding: '7px 8px', borderBottom: `1px solid ${LINE}`, fontVariantNumeric: 'tabular-nums' }}>
              <span style={{ color: MUTED }}>{i + 1}</span>
              <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{l.description || '—'}</span>
                {doc.isTaxInvoice && <span style={{ color: MUTED, fontSize: 8.5 }}>{l.taxRateBp ? `VAT ${l.taxRateBp / 100}%` : 'No VAT'}</span>}
              </span>
              <span style={{ textAlign: 'right' }}>{qty(l.quantity)}{l.unit ? ` ${l.unit}` : ''}</span>
              <span style={{ textAlign: 'right' }}>{num(l.unitPriceMinor)}</span>
              <span style={{ textAlign: 'right', color: MUTED }}>{l.discountMinor ? num(l.discountMinor) : '—'}</span>
              <span style={{ textAlign: 'right', fontWeight: 500 }}>{num(lineAmount(l))}</span>
            </div>
          ))}
          {!doc.lines.length && (
            <div style={{ padding: '16px 8px', color: '#8A8A96', textAlign: 'center', borderBottom: `1px solid ${LINE}` }}>Add a line item to see it here.</div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 220px', gap: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {doc.voidReason && (
              <div style={{ color: '#B3262B', fontWeight: 600 }}>Void: {doc.voidReason}</div>
            )}
            {doc.isTaxInvoice && doc.amountInWords && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Label>AMOUNT IN WORDS</Label>
                <span style={{ fontStyle: 'italic', color: '#2A2A33' }}>{doc.amountInWords}</span>
              </div>
            )}
            {doc.docType === 'INVOICE' && !isVoid && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ width: 58, height: 58, flex: 'none', padding: 3, outline: '1px solid #E1E1E7', background: '#fff' }}>
                  {doc.shareUrl
                    ? <QRCode value={doc.shareUrl} size={52} style={{ width: 52, height: 52 }} />
                    : <div style={{ width: 52, height: 52, background: 'repeating-linear-gradient(45deg,#F3F3F6,#F3F3F6 4px,#E7E7EC 4px,#E7E7EC 8px)' }} />}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                  <Label>PAY THIS INVOICE</Label>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: accent, fontWeight: 600, overflowWrap: 'anywhere' }}>
                    {doc.shareUrl ? doc.shareUrl.replace(/^https?:\/\//, '') : 'Link created on issue'}
                  </span>
                  {bank && <span style={{ color: SUB }}>{[bank.bankName, bank.branch].filter(Boolean).join(' · ')}</span>}
                  {bank && <span style={{ color: SUB }}>{bank.accountName} · {bank.accountNumber}</span>}
                </div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontVariantNumeric: 'tabular-nums' }}>
            {totals.map((t, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, color: t.muted ? MUTED : '#131318', fontWeight: t.strong ? 700 : 400, fontSize: t.top ? 10.5 : 9.5, padding: t.top ? '5px 0 0' : 0, borderTop: t.top ? `1px solid ${LINE}` : 'none', marginTop: t.top ? 3 : 0 }}>
                <span>{t.l}</span><span>{t.v}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 4, padding: '7px 9px', borderRadius: 5, background: accent, color: '#fff', fontWeight: 700, fontSize: 11 }}>
              <span>{balanceLabel}</span><span>{isVoid ? '—' : money(balance, cur)}</span>
            </div>
          </div>
        </div>

        {(doc.notes || doc.terms) && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}><Label>NOTES</Label><span style={{ color: SUB, whiteSpace: 'pre-wrap' }}>{doc.notes || '—'}</span></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}><Label>TERMS</Label><span style={{ color: SUB, whiteSpace: 'pre-wrap' }}>{doc.terms || '—'}</span></div>
          </div>
        )}

        <div style={{ marginTop: 'auto', paddingTop: 10, borderTop: `1px solid ${LINE}`, display: 'flex', justifyContent: 'space-between', gap: 12, color: MUTED, fontSize: 8 }}>
          <span>{branding.footerText || ''}</span><span>Powered by Netlise</span>
        </div>
      </div>
    </div>
  );
}
